import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, Plus, PlayCircle, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CreateEntryModal from './CreateEntryModal';
import { t } from '../i18n';

/**
 * Bottom-Navbar fürs Web auf Mobile (Marcel 2026-09-26: „wie in der App“):
 * Feed · Suchen · + (Eintrag erstellen) · Live · Profil. Nur unter md sichtbar,
 * fest am unteren Rand mit Safe-Area, flach mit Blur, Türkis nur als Akzent.
 * Ausgeblendet auf Seiten mit eigener Fußleiste (Verwaltung/Erstellen/Bearbeiten,
 * Admin) und auf den Auth-Seiten.
 */
export type BottomNavKey = 'feed' | 'suchen' | 'live' | 'profil';

const HIDDEN_PREFIXES = ['/admin', '/verwaltung/', '/login', '/register', '/passwort-vergessen'];

/** PURE: Auf welchen Pfaden erscheint die Leiste? */
export function bottomNavVisible(pathname: string): boolean {
  const p = (pathname || '/').toLowerCase();
  if (HIDDEN_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix))) return false;
  if (/^\/turnier\/[^/]+\/anmelden/.test(p)) return false;
  return true;
}

/** PURE: aktiver Eintrag je Pfad */
export function bottomNavActiveKey(pathname: string): BottomNavKey | null {
  const p = (pathname || '/').toLowerCase();
  if (p === '/') return 'feed';
  // „Suchen“ führt im Web zur „In der Nähe“-Ansicht (Marcel 2026-09-26)
  if (p === '/in-der-naehe' || p.startsWith('/in-der-naehe/')) return 'suchen';
  if (p === '/spiele' || p.startsWith('/spiele/')) return 'live';
  if (p === '/profil' || p.startsWith('/profil/')) return 'profil';
  return null;
}

// Kompakt (Marcel 2026-09-26): Leiste 52 px, Schrift 10 px, Icons 20 px
const ITEM_CLS = 'flex min-w-0 flex-1 flex-col items-center justify-center gap-[3px] py-1 text-[10px] font-medium leading-none transition-colors';

const BottomNav: React.FC = () => {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  if (!bottomNavVisible(pathname)) return null;
  const active = bottomNavActiveKey(pathname);
  const color = (key: BottomNavKey) => (active === key ? 'text-[#3fced1]' : 'text-zinc-400 hover:text-white');

  const items: { key: BottomNavKey; label: string; to: string; Icon: React.ComponentType<{ size?: number; strokeWidth?: number }> }[] = [
    { key: 'feed', label: t('web.component.bottom-nav.feed'), to: '/', Icon: Home },
    { key: 'suchen', label: t('web.component.bottom-nav.suchen'), to: '/in-der-naehe', Icon: Search },
    { key: 'live', label: t('web.component.bottom-nav.live'), to: '/spiele', Icon: PlayCircle },
    { key: 'profil', label: t('web.component.bottom-nav.profil'), to: isAuthenticated ? '/profil' : '/login', Icon: User },
  ];
  const renderItem = (item: typeof items[number]) => (
    <Link
      key={item.key}
      to={item.to}
      className={`${ITEM_CLS} ${color(item.key)}`}
      aria-current={active === item.key ? 'page' : undefined}
      data-testid={`bottom-nav-${item.key}`}
    >
      <item.Icon size={20} strokeWidth={active === item.key ? 2.3 : 1.7} />
      <span>{item.label}</span>
    </Link>
  );

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-800 bg-zinc-950/90 backdrop-blur-md md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label={t('web.component.bottom-nav.feed')}
        data-testid="bottom-nav"
      >
        <div className="mx-auto flex h-[52px] max-w-3xl items-stretch px-2">
          {items.slice(0, 2).map(renderItem)}
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex min-w-0 flex-1 items-center justify-center"
            aria-label={t('web.component.bottom-nav.erstellen')}
            data-testid="bottom-nav-create"
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-xl text-black shadow-[0_2px_8px_rgba(63,206,209,0.4)]"
              style={{ background: 'linear-gradient(135deg, #3fced1 0%, #32b4b9 100%)' }}
            >
              <Plus size={22} strokeWidth={2.4} />
            </span>
          </button>
          {items.slice(2).map(renderItem)}
        </div>
      </nav>
      <CreateEntryModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
};

export default BottomNav;
